import React from 'react'

import { InfoOutlined } from '@material-ui/icons'
import { Input, Select, Tooltip } from '@baseV2'
import { strings, enquiryInputType, InputType_Unit, Label_InputType } from '@constants'
import { decimalSeparatedNumber, displayAmount } from '@utils'

import { ItemCardControlField, ItemCardFieldLabel, ItemCardFieldValue, ItemCardProductMeta, IfcTrigger, IfcTooltipList } from './itemCardStyles'

// Display unit for each item input type (weight is shown in MT; enquiry items without a type are weight).
const QUANTITY_UNIT = {
	[enquiryInputType.WEIGHT]: 'MT',
	[enquiryInputType.PIECES]: 'PCS',
	[enquiryInputType.LENGTH]: 'MTR',
	[enquiryInputType.SET]: 'SET',
	[enquiryInputType.NUMBER]: 'NOS',
	[enquiryInputType.POWER]: 'KW',
}
const quantityUnit = (inputType) => QUANTITY_UNIT[inputType ?? enquiryInputType.WEIGHT] ?? 'MT'

const ItemCardLabeledSelect = ({ label, ...props }) => (
	<ItemCardControlField>
		<ItemCardFieldLabel>{label}</ItemCardFieldLabel>
		<Select {...props} />
	</ItemCardControlField>
)

const ItemCardLabeledInput = ({ label, ...props }) => (
	<ItemCardControlField>
		<ItemCardFieldLabel>{label}</ItemCardFieldLabel>
		<Input {...props} />
	</ItemCardControlField>
)

// Reusable Quote line-item card, shared between Buyer Quotes and Supplier Quotes (Admin Manage
// Enquiry). Purely presentational — it never touches redux/reducer state or itemId; the caller
// supplies already-resolved values and already-curried handlers, and injects its own page-specific
// row/grid container components so each page's existing desktop layout is preserved exactly.
const ItemCard = ({
	index,

	RowComponent,
	FieldsWrapperComponent,
	StaticFieldsComponent,
	FieldColComponent,
	InputsComponent,

	productName,
	hsnCode,
	itemInputType,
	requiredWeight,

	brands,
	brand,

	brandOptions,
	brandValue,
	onBrandChange,

	quoteInputType,
	serviceableWeight,
	onServiceableWeightChange,

	rateValue,
	onRateChange,
	rateMin,

	ifcPrices,
	truncateSupplierName,
	disabled = false,
}) => {
	const FieldsWrapper = FieldsWrapperComponent || React.Fragment

	const isRequiredWeight = itemInputType === enquiryInputType.WEIGHT || itemInputType === null
	const requiredWeightDisplay = isRequiredWeight ? decimalSeparatedNumber(requiredWeight / 1000) : requiredWeight

	const brandNames = brands?.length ? brands : brand ? [brand] : []
	const visibleBrandsText = brandNames.length ? brandNames.slice(0, 2).join(', ') : 'ANY'
	const hasMoreBrands = brandNames.length > 2

	const isServiceableWeightKg = quoteInputType === enquiryInputType.WEIGHT || quoteInputType === null
	const serviceableWeightDisplay = serviceableWeight ? (isServiceableWeightKg ? serviceableWeight / 1000 : serviceableWeight) : serviceableWeight

	return (
		<RowComponent>
			<FieldsWrapper>
				<StaticFieldsComponent>
					<FieldColComponent>
						<ItemCardFieldLabel>{strings('product')}</ItemCardFieldLabel>
						<ItemCardFieldValue>{productName ?? '--'}</ItemCardFieldValue>
						<Tooltip content={hasMoreBrands ? brandNames.join(', ') : null} block>
							<ItemCardProductMeta>
								{visibleBrandsText}
								{hasMoreBrands ? '…' : ''}
							</ItemCardProductMeta>
						</Tooltip>
					</FieldColComponent>
					<FieldColComponent>
						<ItemCardFieldLabel>{strings('hsn', 'code')}</ItemCardFieldLabel>
						<ItemCardFieldValue>{hsnCode ?? '--'}</ItemCardFieldValue>
					</FieldColComponent>
					<FieldColComponent>
						<ItemCardFieldLabel>{strings('quantity')}</ItemCardFieldLabel>
						<ItemCardFieldValue>{(requiredWeightDisplay ?? '') !== '' ? `${requiredWeightDisplay} ${quantityUnit(itemInputType)}` : '--'}</ItemCardFieldValue>
					</FieldColComponent>
				</StaticFieldsComponent>
				<InputsComponent>
					<ItemCardLabeledSelect
						id={`quote-brand-select-${index}`}
						label={strings('brand')}
						displayKey='name'
						primaryKey='id'
						placeholder={strings('placeholder_select_brand')}
						data={brandOptions}
						value={{ name: brandValue, id: brandValue }}
						onChange={onBrandChange}
						createNewOption={true}
						disabled={disabled}
						escapeOverflow
					/>
					<ItemCardLabeledInput
						id={`quote-serviceable-weight-${index}`}
						label={strings('quantity')}
						type='number'
						placeholder={strings('enter_serviceable_weight')}
						value={serviceableWeightDisplay}
						onChange={(e) => {
							const val = e.target.value
							// cleared field → null, never NaN (keeps the total at 0 instead of NaN)
							const _val = val === '' ? null : isServiceableWeightKg ? parseFloat(val) * 1000 : val
							onServiceableWeightChange(_val)
						}}
						disabled={disabled}
					/>
					<ItemCardLabeledInput
						id={`quote-per-kg-${index}`}
						label={Label_InputType(quoteInputType, InputType_Unit.RATE)}
						type='number'
						placeholder={isServiceableWeightKg ? strings('placeholder_enter_quote_per_kg') : `Enter quote/${quantityUnit(quoteInputType).toLowerCase()}`}
						value={rateValue}
						onChange={(e) => onRateChange(e.target.value)}
						min={rateMin}
						disabled={disabled}
					/>
					{ifcPrices?.length > 0 && (
						<ItemCardControlField>
							<ItemCardFieldLabel aria-hidden='true'>&nbsp;</ItemCardFieldLabel>
							<Tooltip
								variant='light'
								tapToggle
								placement='top'
								content={
									<IfcTooltipList>
										{ifcPrices.map((p, i) => (
											<div key={i}>
												{truncateSupplierName(p.supplierName)}: <b>{displayAmount(p.ifcPrice)}</b> ({strings(p.quoteType)})
											</div>
										))}
									</IfcTooltipList>
								}
							>
								<IfcTrigger>
									<InfoOutlined />
								</IfcTrigger>
							</Tooltip>
						</ItemCardControlField>
					)}
				</InputsComponent>
			</FieldsWrapper>
		</RowComponent>
	)
}

export default ItemCard
